/**
 * 「화면 가까이 왔는가」 (15회차 M33 · Codex 8차 B6) — 홈의 첫 화면 밖 사진을 실제로 미룬다.
 *
 * 예전에는 브라우저 `loading="lazy"` 에 맡겼다. 느린 망에서 Chrome 의 지연 거리는 약 2,500px 이라 홈 전체(1280 2,113px)가
 * 그 안에 들어와, 첫 화면 밖 사진이 모자이크와 같은 순간 · 같은 우선순위로 받아졌다(1280 32장 1,054KB · 375 40장 1,394KB).
 * 이제 사진 주소는 이 관찰자가 「가깝다」 고 할 때만 img 에 넣는다.
 *  · 처음(아직 아무것도 굴리지 않음) — **화면 안만**. 조상의 overflow 로 잘린 부분(휴대폰 가로 띠의 오른쪽)도 밖으로 친다.
 *    그래서 첫 진입에서 첫 화면 밖 사진 요청은 0 이다.
 *  · 사용자가 한 번이라도 굴리면 — 위아래 1화면, 가로 띠는 좌우 1띠 폭까지 미리(내려 볼 때 빈 칸이 오래 남지 않게).
 *    「굴림」 은 페이지가 맨 위에서 내려간 scroll · 휠 · 손가락 끌기(touchmove)다. 가로 띠의 scroll 은 세지 않는다 —
 *    휴대폰 띠(scroll-snap)는 처음 그릴 때 스스로 2px 맞춰지며 scroll 을 내 첫 진입부터 넓어졌다(실측 375: 6줄 48장).
 *    맨 위로 되돌리는 scroll(다른 화면에서 홈으로)도 세지 않는다.
 * 범위(NearScope)마다 따로 센다 — 다른 화면에서 굴린 것이 다음 홈 첫 진입을 넓히지 않는다.
 * `hold` — 먼저 받을 사진(홈 모자이크)이 다 올 때까지 「사진」 구독(afterHero)은 가까워도 기다린다. 느린 망에서 첫 화면 색 계열 사진이
 *   모자이크와 대역을 나눠 모자이크가 늦게 채워졌다(fetchpriority 만으로는 HTTP/1.1 연결 6개를 먼저 잡은 요청 순서를 못 바꾼다).
 *   목록(JSON) 구독은 기다리지 않는다 — 모자이크가 끝나면 곧바로 사진 주소를 넣을 수 있게.
 * 범위 밖에서 부르거나 IntersectionObserver 가 없으면 늘 「가깝다」(미루지 않는다).
 * 인쇄(Codex 9차 미확인 → 16회차 묶음 D 확인): 인쇄에는 굴림이 없어 미룬 칸이 옅은 면 · 골격으로 찍혔다(1280 홈 PDF: 옅은 면 15 · 골격 32).
 *   beforeprint · matchMedia('print') 가 오면 열린 범위의 미루기와 hold 를 모두 풀고(flushSync — 인쇄 배치 전에 사진 주소가 들어가게),
 *   그 범위는 그 뒤에 붙는 구독(늦게 온 색 계열 줄의 견본)도 바로 부른다. 다음에 여는 홈은 다시 미룬다.
 */
import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

type Sub = { fn: () => void; afterHero: boolean };

class NearWatcher {
  private wide = false;
  /** 인쇄로 모두 풀었다 — 이 범위는 더 미루지 않는다 */
  private all = false;
  private io: IntersectionObserver | null = null;
  private readonly subs = new Map<Element, Sub>();
  /** 가까워졌지만 먼저 받을 사진을 기다리는 구독 */
  private readonly waiting = new Map<Element, () => void>();
  constructor(private held: boolean) {}

  /** 먼저 받을 사진이 다 왔는가 — 풀면 기다리던 구독을 부른다 */
  hold(on: boolean): void {
    if (this.all) return;
    this.held = on;
    if (on) return;
    const fs = [...this.waiting.values()];
    this.waiting.clear();
    fs.forEach((f) => f());
  }
  private readonly onScroll = (e: Event) => { if (e.target === document && window.scrollY > 0) this.widen(); };
  private readonly onInput = () => this.widen();

  private listen(on: boolean): void {
    const f = on ? document.addEventListener.bind(document) : document.removeEventListener.bind(document);
    f('scroll', this.onScroll, { capture: true, passive: true });
    f('wheel', this.onInput, { capture: true, passive: true });
    f('touchmove', this.onInput, { capture: true, passive: true });
  }

  /**
   * 범위가 붙을 때 — StrictMode 의 두 번 실행에서도 다시 붙을 수 있게(구독은 그대로 둔다).
   * 이미 인쇄 매체이면(인쇄 중에 새 화면을 연 경우 · 인쇄 매체를 먼저 켠 자동화) 인쇄 이벤트가 다시 오지 않는다 — 바로 푼다(Codex 10차 ③)
   */
  start(): void {
    live.add(this);
    watchPrint();
    if (this.all) return;
    if (printing()) { this.release(); return; }
    if (!this.wide) this.listen(true);
    this.make();
  }

  stop(): void {
    live.delete(this);
    this.quiet();
  }

  private quiet(): void {
    this.listen(false);
    this.io?.disconnect();
    this.io = null;
  }

  /** 인쇄 — 기다리는 사진 · 아직 먼 구독을 한꺼번에 부른다 */
  release(): void {
    if (this.all) return;
    this.all = true;
    this.held = false;
    this.quiet();
    const fs = [...this.waiting.values(), ...[...this.subs.values()].map((s) => s.fn)];
    this.waiting.clear();
    this.subs.clear();
    fs.forEach((f) => f());
  }

  private make(): void {
    this.io?.disconnect();
    // scrollMargin(Chrome 120+) — 가로 띠 같은 안쪽 스크롤 상자에도 여유를 준다. 모르는 브라우저는 무시한다
    const opts: IntersectionObserverInit & { scrollMargin?: string } = this.wide
      ? { rootMargin: '100% 0px 100% 0px', scrollMargin: '0px 100% 0px 100%', threshold: [0, 0.01] }
      : { rootMargin: '0px', threshold: [0, 0.01] };
    const io = new IntersectionObserver(this.hit, opts);
    this.io = io;
    this.subs.forEach((_, el) => io.observe(el));
  }

  private readonly hit = (es: IntersectionObserverEntry[]) => {
    for (const e of es) {
      // 처음에는 실제로 겹친 면이 있어야 한다(화면 아래 끝에 맞닿기만 한 줄은 첫 화면 밖이다)
      if (!e.isIntersecting || (!this.wide && e.intersectionRatio <= 0)) continue;
      const sub = this.subs.get(e.target);
      if (!sub) continue;
      this.subs.delete(e.target);
      this.io?.unobserve(e.target);
      if (sub.afterHero && this.held) this.waiting.set(e.target, sub.fn); else sub.fn();
    }
  };

  private widen(): void {
    if (this.wide) return;
    this.wide = true;
    this.listen(false);
    if (this.io) this.make();
  }

  watch(el: Element, fn: () => void, afterHero = false): () => void {
    if (this.all) { fn(); return () => undefined; }
    this.subs.set(el, { fn, afterHero });
    this.io?.observe(el);
    return () => { this.subs.delete(el); this.waiting.delete(el); this.io?.unobserve(el); };
  }
}

/** 지금 붙어 있는 범위들 — 인쇄할 때 모두 푼다 */
const live = new Set<NearWatcher>();
let printWatched = false;
function releaseAll(): void {
  if (!live.size) return;
  // 인쇄 배치는 이 이벤트 바로 뒤에 잡힌다 — React 의 다음 그리기를 기다리지 않고 지금 사진 주소를 넣는다
  flushSync(() => live.forEach((w) => w.release()));
}
/** 지금 인쇄 매체인가 */
function printing(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('print').matches;
}
function watchPrint(): void {
  if (printWatched || typeof window === 'undefined') return;
  printWatched = true;
  window.addEventListener('beforeprint', releaseAll);
  const mq = window.matchMedia?.('print');
  const on = (e: MediaQueryListEvent) => { if (e.matches) releaseAll(); };
  if (mq?.addEventListener) mq.addEventListener('change', on);
  else mq?.addListener?.(on);
}

const Ctx = createContext<NearWatcher | null>(null);

/** 이 아래의 useNear 가 한 관찰자를 함께 쓴다 — 홈 한 화면이 하나. hold 동안 사진 구독(afterHero)은 기다린다 */
export function NearScope({ children, hold = false }: { children: ReactNode; hold?: boolean }) {
  const [w] = useState(() => (typeof IntersectionObserver === 'undefined' ? null : new NearWatcher(hold)));
  useEffect(() => {
    if (!w) return;
    w.start();
    return () => w.stop();
  }, [w]);
  useEffect(() => { w?.hold(hold); }, [w, hold]);
  return createElement(Ctx.Provider, { value: w }, children);
}

/**
 * 요소가 화면 가까이 온 적이 있는가(한 번 true 면 그대로). 돌려준 ref 를 그 요소에 붙인다.
 * on=false 면 처음부터 true — 미루지 않는 자리(첫 화면 모자이크 · 범위 밖). afterHero — 사진이면 true(범위의 hold 동안 기다린다)
 */
export function useNear<T extends Element>(on = true, afterHero = false): [(el: T | null) => void, boolean] {
  const w = useContext(Ctx);
  const [near, setNear] = useState(!w || !on);
  const [el, setEl] = useState<T | null>(null);
  useEffect(() => {
    if (near || !w || !el) return;
    return w.watch(el, () => setNear(true), afterHero);
  }, [w, el, near, afterHero]);
  return [setEl, near];
}
